import { useEffect, useRef, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Send, Square, ChevronDown, Check, Loader2 } from "lucide-react";

/* ─── Types ─── */

export interface PromptInputModel {
  id: string;
  name: string;
  description: string;
}

export interface PromptInputChip {
  label: string;
  value: string;
}

export interface PromptInputProps {
  // Core (required)
  value: string;
  onValueChange: (value: string) => void;
  onSubmit: () => void;

  // Display
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;

  // Model picker (shown when models provided)
  models?: PromptInputModel[];
  selectedModel?: string;
  onModelChange?: (modelId: string) => void;

  // Suggestion chips (shown above input when provided, hidden during loading)
  chips?: PromptInputChip[];
  onChipSelect?: (chip: PromptInputChip) => void;

  // Slots
  loadingContent?: ReactNode;
  headerContent?: ReactNode;

  // Abort (streaming)
  onAbort?: () => void;

  // Send button
  sendIcon?: ReactNode;
  allowEmptySubmit?: boolean;

  // Ref
  textareaRef?: React.Ref<HTMLTextAreaElement>;
}

/* ─── Internal: Model Picker ─── */

function ModelPicker({
  models,
  value,
  onChange,
}: {
  models: PromptInputModel[];
  value: string | undefined;
  onChange: (modelId: string) => void;
}) {
  if (models.length === 0) return null;

  const selected = models.find((m) => m.id === value) ?? models[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
          {selected.name}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={6} className="w-56">
        {models.map((m) => (
          <DropdownMenuItem
            key={m.id}
            onClick={() => onChange(m.id)}
            className="flex items-start gap-2 py-2"
          >
            <Check
              className={cn(
                "mt-0.5 h-3.5 w-3.5 shrink-0",
                m.id === selected.id ? "opacity-100" : "opacity-0",
              )}
            />
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium">{m.name}</span>
              <span className="text-xs text-muted-foreground">
                {m.description}
              </span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ─── Internal: Chip Row ─── */

function ChipRow({
  chips,
  onSelect,
}: {
  chips: PromptInputChip[];
  onSelect: (chip: PromptInputChip) => void;
}) {
  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map((chip) => (
        <button
          key={chip.value}
          onClick={() => onSelect(chip)}
          className={cn(
            "rounded-full border border-border/60 px-3 py-1 text-xs text-muted-foreground",
            "transition-colors hover:border-border hover:bg-accent hover:text-accent-foreground",
          )}
        >
          {chip.label}
        </button>
      ))}
    </div>
  );
}

/* ─── PromptInput ─── */

export function PromptInput({
  value,
  onValueChange,
  onSubmit,
  placeholder = "Describe a change...",
  disabled = false,
  isLoading = false,
  className,
  models,
  selectedModel,
  onModelChange,
  chips,
  onChipSelect,
  onAbort,
  loadingContent,
  headerContent,
  sendIcon,
  allowEmptySubmit = false,
  textareaRef: externalRef,
}: PromptInputProps) {
  const internalRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const el = internalRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 140) + "px";
  }, [value]);

  const mergedRef = useMergedRef(externalRef, internalRef);

  const canSubmit =
    !disabled && !isLoading && (allowEmptySubmit || !!value.trim());

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (canSubmit) onSubmit();
    }
  };

  const defaultChipHandler = (chip: PromptInputChip) => {
    onValueChange(chip.value);
  };

  const chipHandler = onChipSelect ?? defaultChipHandler;

  const hasChips = chips && chips.length > 0;
  const hasModels = models && models.length > 0;

  return (
    <div className={className}>
      {/* Header slot */}
      {headerContent}

      {/* Chips / loading content area */}
      {(hasChips || loadingContent) && (
        <div className="mb-2">
          {isLoading && loadingContent ? (
            loadingContent
          ) : hasChips && !isLoading ? (
            <ChipRow chips={chips} onSelect={chipHandler} />
          ) : null}
        </div>
      )}

      {/* Input container */}
      <div
        className={cn(
          "rounded-xl border bg-muted/40 transition-[border-color,box-shadow]",
          "focus-within:border-primary/40 focus-within:ring-2 focus-within:ring-primary/10",
        )}
      >
        <textarea
          ref={mergedRef}
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          disabled={disabled || isLoading}
          className={cn(
            "w-full resize-none bg-transparent px-3.5 pt-3 pb-1.5 text-sm",
            "placeholder:text-muted-foreground",
            "focus:outline-none",
            "disabled:opacity-50",
          )}
          style={{ minHeight: 36, maxHeight: 140 }}
        />

        {/* Toolbar row */}
        <div className="flex items-center gap-1 px-2 pb-2">
          {hasModels && onModelChange && (
            <ModelPicker
              models={models}
              value={selectedModel}
              onChange={onModelChange}
            />
          )}

          <div className="ml-auto flex items-center gap-1.5">
            {isLoading && onAbort ? (
              <SimpleTooltip content="Stop generating">
                <Button
                  size="icon"
                  variant="outline"
                  className="h-7 w-7 rounded-lg"
                  onClick={onAbort}
                  aria-label="Stop generating"
                >
                  <Square className="h-3 w-3" />
                </Button>
              </SimpleTooltip>
            ) : (
              <>
                <kbd className="hidden rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground sm:inline-block">
                  ⌘↵
                </kbd>
                <SimpleTooltip content="Send message (⌘↵)">
                  <Button
                    size="icon"
                    variant={canSubmit ? "default" : "ghost"}
                    className={cn(
                      "h-7 w-7 rounded-lg transition-all",
                      !canSubmit && "text-muted-foreground",
                    )}
                    onClick={() => {
                      if (canSubmit) onSubmit();
                    }}
                    disabled={!canSubmit}
                    aria-label="Send message"
                  >
                    {isLoading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      (sendIcon ?? <Send className="h-3.5 w-3.5" />)
                    )}
                  </Button>
                </SimpleTooltip>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Hooks ─── */

function useMergedRef<T>(
  ...refs: (React.Ref<T> | undefined)[]
): React.RefCallback<T> {
  return (instance: T | null) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === "function") {
        ref(instance);
      } else {
        (ref as React.MutableRefObject<T | null>).current = instance;
      }
    }
  };
}
